import type { ExportedInstance, ExportedInstanceItems, InstanceSummary } from "@keydials/api-client";

/**
 * "Download my data" on the access-ended screen (KEY-912).
 *
 * There is no single all-my-data route, so this assembles one file from the ones that exist and that the access
 * gate leaves open (KEY-911 gates opening a dial, not listing or exporting it):
 *
 * - `GET /api/instances` — the dials this person can see.
 * - `GET /api/instances/{id}/export-instance` — the whole dial: every status, attribute and history entry. Needs
 *   instance.manage, which an organisation's own administrator holds.
 * - `GET /api/instances/{id}/items/export` — the dial's items, on instance.read. The fallback for somebody who
 *   holds the dial without managing it, so they still leave with what they put in.
 *
 * A dial neither will give is listed under `skipped` rather than failing the whole download: one refusal should not
 * cost somebody every other dial. Documents themselves are not inside — the dial export names them; the files stay
 * where they are.
 */
export interface MyDataDeps {
  listInstances: () => Promise<InstanceSummary[]>;
  exportInstance: (id: string) => Promise<ExportedInstance>;
  exportInstanceItems: (id: string) => Promise<ExportedInstanceItems>;
}

export type ExportedDial =
  | { id: string; name: string; organisationName: string; kind: "full"; data: ExportedInstance }
  | { id: string; name: string; organisationName: string; kind: "items"; data: ExportedInstanceItems };

export interface MyDataFile {
  exportedAtUtc: string;
  dials: ExportedDial[];
  /** Dials neither export would give this person. */
  skipped: { id: string; name: string }[];
}

export async function collectMyData(deps: MyDataDeps, now: Date = new Date()): Promise<MyDataFile> {
  const listed = await deps.listInstances();
  const dials: ExportedDial[] = [];
  const skipped: { id: string; name: string }[] = [];

  // One at a time: this is somebody's whole account, and a burst of whole-dial exports is the heaviest thing
  // the API serves. A few seconds longer is a fair price for not timing out half of them.
  for (const dial of listed) {
    const base = { id: dial.id, name: dial.name, organisationName: dial.organisationName };
    try {
      dials.push({ ...base, kind: "full", data: await deps.exportInstance(dial.id) });
      continue;
    } catch {
      // Not theirs to export whole — try the items they can read.
    }
    try {
      dials.push({ ...base, kind: "items", data: await deps.exportInstanceItems(dial.id) });
    } catch {
      skipped.push({ id: dial.id, name: dial.name });
    }
  }

  return { exportedAtUtc: now.toISOString(), dials, skipped };
}

/** The file's name: who and when, safe on every file system. */
export function myDataFileName(organisationName: string | null | undefined, now: Date = new Date()): string {
  const who = (organisationName ?? "my").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "my";
  return `${who}-keydials-data-${now.toISOString().slice(0, 10)}.json`;
}
